import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { extractWorkOrder, createProductionJob } from '../../services/api';
import { nextDispatchDate } from './stages';
import { toast } from 'sonner';
import { Upload, Plus, Trash2 } from 'lucide-react';

const emptyItem = () => ({ code: '', item_type: 'window', size: '', quantity: 1 });

const NewJobDialog = ({ open, onOpenChange, onCreated }) => {
  const [mode, setMode] = useState('manual');
  const [customer, setCustomer] = useState('');
  const [dispatchDay, setDispatchDay] = useState('wednesday');
  const [items, setItems] = useState([emptyItem()]);
  const [woFile, setWoFile] = useState({ file_path: null, file_name: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setMode('manual'); setCustomer(''); setDispatchDay('wednesday');
    setItems([emptyItem()]); setWoFile({ file_path: null, file_name: null });
  };

  const setItem = (idx, field, val) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [field]: val };
    setItems(next);
  };
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => { const next = items.slice(); next.splice(idx, 1); setItems(next); };

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await extractWorkOrder(file);
      const raw = res.data.items || [];
      setWoFile({ file_path: res.data.file_path, file_name: res.data.file_name });
      if (raw.length > 0) { setItems(raw); toast.success(`Extracted ${raw.length} item(s)`); }
      else toast.warning('No items extracted — add them manually');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreate = async () => {
    if (!customer.trim()) { toast.error('Enter a customer / project name'); return; }
    const valid = items.filter((i) => i.code || i.size);
    if (valid.length === 0) { toast.error('Add at least one door/window item (code or size)'); return; }
    setSaving(true);
    try {
      const units = valid.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
      await createProductionJob({
        customer: customer.trim(),
        units,
        items: valid.map((i) => ({ code: String(i.code || ''), item_type: i.item_type === 'door' ? 'door' : 'window', size: String(i.size || ''), quantity: Number(i.quantity) || 1 })),
        dispatch_day: dispatchDay,
        target_dispatch: nextDispatchDate(dispatchDay),
        work_order_file_path: woFile.file_path,
        work_order_file_name: woFile.file_name,
      });
      toast.success('Job created');
      onOpenChange(false);
      reset();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Production Job</DialogTitle>
          <DialogDescription>Upload a work order to auto-extract doors/windows, or add them manually. Then pick the dispatch batch.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">Customer / Project *</Label><Input value={customer} onChange={(e) => setCustomer(e.target.value)} className="mt-1" data-testid="job-customer" /></div>
          <div>
            <Label className="text-xs text-muted-foreground">Target Dispatch</Label>
            <Select value={dispatchDay} onValueChange={setDispatchDay}>
              <SelectTrigger className="mt-1" data-testid="job-dispatch-day"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="wednesday">Wednesday ({nextDispatchDate('wednesday')})</SelectItem>
                <SelectItem value="saturday">Saturday ({nextDispatchDate('saturday')})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 border-b">
          <button className={`px-3 py-2 text-sm border-b-2 ${mode === 'manual' ? 'border-accent font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setMode('manual')} data-testid="job-mode-manual">Manual</button>
          <button className={`px-3 py-2 text-sm border-b-2 ${mode === 'upload' ? 'border-accent font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setMode('upload')} data-testid="job-mode-upload">Upload Work Order</button>
        </div>

        {mode === 'upload' && (
          <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/30" data-testid="wo-upload-label">
            <Upload size={24} className="text-muted-foreground mb-2" />
            <span className="text-sm text-muted-foreground">{uploading ? 'Extracting…' : (woFile.file_name || 'Click to upload CSV, Excel, PDF or image')}</span>
            <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp" onChange={handleUpload} disabled={uploading} data-testid="wo-file-input" />
          </label>
        )}

        <div className="border rounded overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50"><tr>
              <th className="px-3 py-2 text-left text-xs font-medium">Code / Mark</th>
              <th className="px-3 py-2 text-left text-xs font-medium">Type</th>
              <th className="px-3 py-2 text-left text-xs font-medium">Size</th>
              <th className="px-3 py-2 text-left text-xs font-medium">Qty</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody className="divide-y">
              {items.map((it, idx) => (
                <tr key={idx} data-testid={`job-item-${idx}`}>
                  <td className="px-2 py-1"><Input value={it.code} onChange={(e) => setItem(idx, 'code', e.target.value)} className="h-8" /></td>
                  <td className="px-2 py-1">
                    <select value={it.item_type} onChange={(e) => setItem(idx, 'item_type', e.target.value)} className="h-8 border rounded text-xs px-1" data-testid={`job-item-type-${idx}`}>
                      <option value="window">Window</option>
                      <option value="door">Door</option>
                    </select>
                  </td>
                  <td className="px-2 py-1"><Input value={it.size} onChange={(e) => setItem(idx, 'size', e.target.value)} className="h-8" placeholder="W x H" /></td>
                  <td className="px-2 py-1"><Input type="number" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} className="h-8 w-20" /></td>
                  <td className="px-2 py-1"><button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" onClick={addItem} data-testid="job-add-item"><Plus size={14} className="mr-1" />Add Item</Button>

        <Button className="w-full btn-accent" onClick={handleCreate} disabled={saving} data-testid="create-job-btn">{saving ? 'Creating…' : 'Create Job'}</Button>
      </DialogContent>
    </Dialog>
  );
};

export default NewJobDialog;
